import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import MultiSelectFilter from '../components/MultiSelectFilter';
import { LINKEDIN_PAGE_URL, trackFollowClick } from '../utils/analytics';
import { usePageMeta } from '../hooks/usePageMeta';

interface CatalogData {
  categories: { category_id: string; category_description: string }[];
  types: { type_id: string; type_description: string }[];
}

export default function SubscribePage() {
  usePageMeta({ title: 'Subscribe', noindex: true });
  /* Prefilled by the landing-page hero form (/subscribe?email=…) */
  const [email, setEmail] = useState(
    () => new URLSearchParams(window.location.search).get('email') || '',
  );
  const [name, setName] = useState('');
  const [rasffDaily, setRasffDaily] = useState(true);
  const [weeklyDigest, setWeeklyDigest] = useState(true);
  const [stageAlerts, setStageAlerts] = useState(false);
  const [incidentAlerts, setIncidentAlerts] = useState(true);
  const [productTypes, setProductTypes] = useState<string[]>(['food', 'feed', 'food contact material']);
  const [productCategories, setProductCategories] = useState<string[]>([]);
  const [catalog, setCatalog] = useState<CatalogData | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/public/product-categories`)
      .then(r => r.json())
      .then(data => setCatalog(data))
      .catch(() => {});
  }, []);

  const categoryOptions = (catalog?.categories || [])
    .filter(c => c.category_description)
    .map(c => ({ value: c.category_description, label: c.category_description }));

  const typeLabels: Record<string, string> = {
    'food': 'Food',
    'feed': 'Feed',
    'food contact material': 'Food Contact Material (FCM)',
  };

  function toggleType(type: string) {
    setProductTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const res = await fetch(`${API_BASE_URL}/api/public/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          name: name.trim() || null,
          preferences: {
            rasff_daily: rasffDaily,
            legislation_weekly: weeklyDigest,
            legislation_stage_alerts: stageAlerts,
            major_incident_alerts: incidentAlerts,
            product_categories: productCategories,
            product_types: productTypes,
          },
        }),
      });

      if (res.ok) {
        setSuccess(true);
      } else {
        const data = await res.json();
        setError(data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setError('Could not reach the server. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  }

  if (success) {
    return (
      <div className="subscribe-page">
        <div className="subscribe-card" style={{ textAlign: 'center' }}>
          <div className="subscribe-card__logo">FS</div>
          <h1 className="subscribe-card__title">Food Safety Digest</h1>
          <div className="subscribe-success">
            <div className="subscribe-success__icon">&#10003;</div>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--color-primary)', margin: '12px 0 8px' }}>
              You're subscribed!
            </h2>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem', lineHeight: 1.6 }}>
              You'll receive {rasffDaily ? 'daily RASFF notifications' : ''}{rasffDaily && weeklyDigest ? ' and ' : ''}{weeklyDigest ? 'a weekly food safety digest' : ''} at <strong>{email}</strong>.
            </p>
            <Link to="/rasff" className="subscribe-card__cta" style={{ marginTop: 20, display: 'inline-block' }}>
              Explore the dashboard
            </Link>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.82rem', marginTop: 18 }}>
              Prefer it in your feed?{' '}
              <a
                href={LINKEDIN_PAGE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="admin-link"
                onClick={() => trackFollowClick('subscribe_confirm')}
              >
                Follow Food Safety Digest on LinkedIn
              </a>
              .
            </p>

          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="subscribe-page">
      <div className="subscribe-card">
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div className="subscribe-card__logo">FS</div>
          <h1 className="subscribe-card__title">Food Safety Digest</h1>
          <p className="subscribe-card__tagline">
            Get RASFF notifications delivered to your inbox
          </p>
        </div>

        <form onSubmit={handleSubmit} className="subscribe-form">
          {/* Email */}
          <div className="subscribe-form__field">
            <label className="subscribe-form__label">Email address *</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              className="subscribe-form__input"
            />
          </div>

          {/* Name */}
          <div className="subscribe-form__field">
            <label className="subscribe-form__label">Name <span style={{ fontWeight: 400, color: 'var(--color-text-tertiary)' }}>(optional)</span></label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Your name"
              className="subscribe-form__input"
            />
          </div>

          {/* Notification types */}
          <div className="subscribe-form__field">
            <label className="subscribe-form__label">Notifications</label>
            <div className="subscribe-form__checks">
              <label className="subscribe-form__check">
                <input type="checkbox" checked={rasffDaily} onChange={e => setRasffDaily(e.target.checked)} />
                <div>
                  <span className="subscribe-form__check-label">Daily RASFF digest</span>
                  <span className="subscribe-form__check-desc">All new RASFF notifications from the past 24 hours</span>
                </div>
              </label>
              <label className="subscribe-form__check">
                <input type="checkbox" checked={weeklyDigest} onChange={e => setWeeklyDigest(e.target.checked)} />
                <div>
                  <span className="subscribe-form__check-label">Weekly food safety digest</span>
                  <span className="subscribe-form__check-desc">Weekly briefing covering legislation, RASFF, recalls &amp; scientific updates</span>
                </div>
              </label>
              <label className="subscribe-form__check">
                <input type="checkbox" checked={stageAlerts} onChange={e => setStageAlerts(e.target.checked)} />
                <div>
                  <span className="subscribe-form__check-label">Legislation stage alerts</span>
                  <span className="subscribe-form__check-desc">Same-day email when a high-impact draft EU act is voted, enters scrutiny, is adopted or is published</span>
                </div>
              </label>
              <label className="subscribe-form__check">
                <input type="checkbox" checked={incidentAlerts} onChange={e => setIncidentAlerts(e.target.checked)} />
                <div>
                  <span className="subscribe-form__check-label">Food safety crisis alerts</span>
                  <span className="subscribe-form__check-desc">Same-day email when a food safety incident crosses the major threshold — confirmed outbreaks, facility closures, multi-country recalls. A handful per year.</span>
                </div>
              </label>
            </div>
          </div>

          {/* Product types */}
          {rasffDaily && (
            <>
              <div className="subscribe-form__field">
                <label className="subscribe-form__label">Product types</label>
                <div className="subscribe-form__checks subscribe-form__checks--row">
                  {Object.entries(typeLabels).map(([val, lbl]) => (
                    <label key={val} className="subscribe-form__check subscribe-form__check--inline">
                      <input type="checkbox" checked={productTypes.includes(val)} onChange={() => toggleType(val)} />
                      <span>{lbl}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Product categories */}
              <div className="subscribe-form__field">
                <label className="subscribe-form__label">
                  Product categories
                  <span style={{ fontWeight: 400, color: 'var(--color-text-tertiary)', fontSize: '0.8rem', marginLeft: 6 }}>
                    Leave empty for all categories
                  </span>
                </label>
                <MultiSelectFilter
                  options={categoryOptions}
                  selected={productCategories}
                  onChange={setProductCategories}
                  placeholder="All categories"
                />
              </div>
            </>
          )}

          {error && (
            <div className="subscribe-form__error">{error}</div>
          )}

          <button type="submit" className="subscribe-card__cta" disabled={submitting}>
            {submitting ? 'Subscribing...' : 'Subscribe'}
          </button>

          <p className="subscribe-form__gdpr">
            By subscribing you agree to receive food safety notifications from FSNU. You can unsubscribe at any time via the link in each email.
          </p>
        </form>
      </div>
    </div>
  );
}
